import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { UserType, ProfileUpdateRequestImpl, Long } from "shared/cubiq-model-multi-asset";
import { BfxUserSettingsService, BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import {
  BfxUserSettingsPageComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import {
  UserPromiseLookupService,
  ActiveUser,
  ProfileSettingKeys,
  PositionBookPromiseLookupService,
  TraderGroupPromiseLookupService,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { BfxAutoCompleteComponentState } from "../bfx-element-library/bfx-autocomplete.component";
import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";
import {
  NativeDesktopSupport,
  ZOOM_VALUES,
  ZOOM_VALUE_SMALL,
  ZOOM_VALUE_STANDARD,
  ZOOM_VALUE_LARGE,
  ZOOM_VALUE_VERY_LARGE,
} from "../bfx-native-desktop/native-desktop-support";

import { environment } from "../../environments/environment";
import { BfxAuthService } from "app/bfx-common/services/bfx-auth.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

const VALUE_ENGLISH: any = { label: "English", value: "en" };
const PROVIDER_LOCALDB: string = "LocalDB";

const THEMES: { label: string; value: string }[] = [
  { label: "theme.dark", value: "dark" },
  { label: "theme.light", value: "classic" },
];
const NO_DEFAULT_PORTFOLIO = { value: null, label: "-" };

@Component({
  selector: ".bfx-general-settings",
  templateUrl: "./html/bfx-general-settings.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxGeneralSettingsComponent extends BfxUserSettingsPageComponent implements OnDestroy {
  public tableHeight: number = 240;
  public activeSubComponent: number = 0;
  public panelHeight: number = 0;
  public portfolio: {
    value: string;
    label: string;
    branch?: string;
    isDefault?: boolean;
  } = null;

  public portfolioSuggestions: any = [];

  public changePasswordEnabled: boolean = false;
  public dataChanged: boolean = false;
  public language: any = VALUE_ENGLISH;
  public languageSuggestions: any = [VALUE_ENGLISH];
  public fontSize: any = ZOOM_VALUE_STANDARD;
  public fontSizes: any = [
    ZOOM_VALUE_SMALL,
    ZOOM_VALUE_STANDARD,
    ZOOM_VALUE_LARGE,
    ZOOM_VALUE_VERY_LARGE,
  ];
  private portfolioState: BfxAutoCompleteComponentState;
  private themeState: BfxAutoCompleteComponentState;
  public ready: boolean = false;
  public isTrader: boolean = false;
  public settingTheme: string = "";
  public nativeDesktop: boolean = false;
  public saveProfileOnLogout: boolean = true;
  public readonly hmr: boolean = environment.hmr;
  public changePasswordAvailable: boolean = false;

  @ViewChildren(BfxAutoCompleteComponent)
  private autocompletes: QueryList<BfxAutoCompleteComponent>;

  @ViewChild("portfolioSelection") portfolioSelection: BfxAutoCompleteComponent;
  @ViewChild("themeSelection") themeSelection: BfxAutoCompleteComponent;
  @ViewChild("fontSizeSelection") fontSizeSelection: BfxAutoCompleteComponent;

  private loadedData: {
    portfolio: any;
    theme: any;
  } = {
    portfolio: null,
    theme: null,
  };
  filteredTheme: any[];
  theme: any;
  private readonly adminRequested: boolean = false;

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public tradeGroupService: TraderGroupPromiseLookupService,
    public systemConfigService: SystemConfigPromiseService,
    public positionBookService: PositionBookPromiseLookupService,
    public userService: UserPromiseLookupService,
    public element: ElementRef,
    public translationService: TranslateService,
    private authService: BfxAuthService,
    public cd: ChangeDetectorRef,
  ) {
    super();
  }

  public initialize(): void {
    THEMES.forEach((theme: any) => (theme.label = this.translationService.instant(theme.label)));

    this.nativeDesktop = NativeDesktopSupport.isNativeDesktopContainer();

    let activeUser: ActiveUser = this.parentComponent.activeUser;

    this.isTrader = activeUser.userTypeId == UserType.TRADER;
    this.ready = true;
    this.changePasswordAvailable =
      activeUser.authProvider == null || activeUser.authProvider == PROVIDER_LOCALDB;
    this.activeSubComponent = this.parentComponent.activeSubCategory;
    this.searchPortfolio({ query: "" });
    this.filterTheme({ query: "" });
    this.searchFontSize({ query: "" });

    this.positionBookService.getAdaptedUserDefaultPositionBook().then((value) => {
      this.portfolio = value;
      this.loadedData.portfolio = value;
    });

    if (
      activeUser.profileSettings &&
      activeUser.profileSettings.has(ProfileSettingKeys.userThemeSettings)
    ) {
      let themeValue = activeUser.profileSettings.get(ProfileSettingKeys.userThemeSettings);
      this.theme = THEMES.find((t) => t.value === themeValue);
      this.loadedData.theme = Object.assign(
        new Object(),
        THEMES.find((t) => t.value === themeValue),
      );
    } else {
      this.theme = THEMES[0];
      this.loadedData.theme = THEMES[0];
    }

    if (
      activeUser.profileSettings &&
      activeUser.profileSettings.has(ProfileSettingKeys.userSaveProfileOnLogout)
    ) {
      let saveProfileOnLogoutStr: string = activeUser.profileSettings.get(
        ProfileSettingKeys.userSaveProfileOnLogout,
      );
      this.saveProfileOnLogout = saveProfileOnLogoutStr === "true";
    } else {
      this.saveProfileOnLogout = true;
    }

    if (
      activeUser.profileSettings &&
      activeUser.profileSettings.has(ProfileSettingKeys.userZoomSetting)
    ) {
      let fontSizeValue: string = activeUser.profileSettings.get(
        ProfileSettingKeys.userZoomSetting,
      );
      this.setZoomValue(fontSizeValue);
    } else {
      this.setZoomValue(ZOOM_VALUE_STANDARD.value);
    }

    // Recalculate dynamic panel height
    if (this.ready) {
      this.panelHeight = this.calculatePanelHeight(this.element);
    }
  }

  /* Recalculates panel height based on resize */
  public calculatePanelHeight(element: ElementRef): number {
    let panelTabHeight = element.nativeElement.querySelector("ul.ui-tabview-nav").offsetHeight;
    return element.nativeElement.clientHeight - panelTabHeight;
  }

  public setTheme(theme: string): void {
    let selectedTheme: any[] = THEMES.filter((value: any) => value.value == theme);
    if (selectedTheme.length == 1) {
      this.theme = selectedTheme[0];
    }
  }

  filterTheme(event) {
    // ??? Flagged for Delete / Replace
    let themeProv = [];
    for (let i = 0; i < THEMES.length; i++) {
      let theme = THEMES[i];
      if (theme.label.toLowerCase().indexOf(event.query.toLowerCase()) == 0) {
        themeProv.push(theme);
      }
    }
    this.filteredTheme = themeProv;
  }

  public onBlurAutocomplete(autocomplete: BfxAutoCompleteComponent): void {
    // TODO consider dropping this.xState for getters or some other design
    if (autocomplete === this.portfolioSelection) {
      if (this.detectPortfolioChange()) {
        this.dataChanged = true;
        this.portfolioState = this.portfolioSelection.state;
        this.parentComponent.validate(this);
      }
    } else if (autocomplete === this.themeSelection) {
      if (this.detectThemeChange()) {
        this.dataChanged = true;
        this.themeState = this.themeSelection.state;
        this.parentComponent.validate(this);
      }
    }
  }

  private detectPortfolioChange(): boolean {
    if (this.loadedData.portfolio == null && this.portfolio == null) {
      return false;
    } else if (this.loadedData.portfolio != null && this.portfolio != null) {
      return this.loadedData.portfolio.value != this.portfolio.value;
    } else {
      return true;
    }
  }

  private detectThemeChange(): boolean {
    if (this.loadedData.theme == null && this.theme == null) {
      return false;
    } else if (this.loadedData.theme != null && this.theme != null) {
      return this.loadedData.theme.value != this.theme.value;
    } else {
      return true;
    }
  }

  public onSelectTheme(data: any): void {
    if (this.detectThemeChange()) {
      this.dataChanged = true;
      this.theme = data;
      this.themeState = this.themeSelection.state;
      this.parentComponent.validate(this);
    }
  }

  public onSaveOnLogoutChange(value: boolean): void {
    this.dataChanged = true;
    this.parentComponent.validate(this);
  }

  public onFocusOut($event): void {
    this.autocompletes.forEach((autocomplete) => {
      if ($event != null && $event.target != null && autocomplete.inputEL != null) {
        if ($event.target === autocomplete.inputEL.nativeElement) {
          // if (autocomplete.value == '' && this.theme == '') {
          //     autocomplete.state = BfxAutoCompleteComponentState.INVALID;
          //     this.themeState = BfxAutoCompleteComponentState.INVALID;
          // }
          this.onBlurAutocomplete(autocomplete);
        }
      }
    });
  }

  portfolioStateChange(event) {
    if (event && this.portfolioState !== event.state) {
      this.portfolioState = event.state;
      if (this.detectPortfolioChange()) {
        this.parentComponent.validate(this);
      }
    }
  }

  themeStateChange(event) {
    if (event && this.themeState !== event.state) {
      this.themeState = event.state;
      if (this.detectThemeChange()) {
        this.dataChanged = true;
        this.parentComponent.validate(this);
      }
    }
  }

  ngOnDestroy() {
    //
  }

  public onSelectPortfolio(data: any): void {
    if (this.detectPortfolioChange()) {
      this.dataChanged = true;
      this.portfolio = data;
      this.portfolioState = this.portfolioSelection.state;
      this.parentComponent.validate(this);
    }
  }

  public searchPortfolio(event: any): void {
    this.positionBookService.getAdaptedPositionBooks(event.query).then((results: Array<any>) => {
      if (
        event != null &&
        typeof event.query == "string" &&
        event.query != "" &&
        Array.isArray(results)
      ) {
        let lcQuery = event.query.toLowerCase();

        if (lcQuery === "-") {
          this.portfolioSuggestions = [NO_DEFAULT_PORTFOLIO];
        } else {
          this.portfolioSuggestions = results.filter(
            (result) =>
              result != null &&
              typeof result.label === "string" &&
              result.label.toLowerCase().includes(lcQuery),
          );
        }
      } else {
        this.portfolioSuggestions = [NO_DEFAULT_PORTFOLIO, ...results];
      }
      this.cd.detectChanges();
    });
  }

  public searchLanguage(event: any): void {
    //
  }

  public searchFontSize(event: any): void {
    let fontSizes = [];

    for (let i = 0; i < ZOOM_VALUES.length; i++) {
      let fontSize: any = ZOOM_VALUES[i];
      if (fontSize.label.toLowerCase().indexOf(event.query.toLowerCase()) == 0) {
        fontSizes.push(fontSize);
      }
    }

    this.fontSizes = fontSizes;
  }

  public onSelectFontSize(data: any): void {
    this.dataChanged = true;
    this.fontSize = data;
    this.parentComponent.validate(this);
  }

  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((resolve, reject) => {
      let state: BfxUserSettingsValidityState;

      if (
        (this.portfolioState == BfxAutoCompleteComponentState.ACTIVE &&
          this.portfolioSelection.noResults) ||
        (this.themeState == BfxAutoCompleteComponentState.ACTIVE && this.themeSelection.noResults)
      ) {
        state = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
      } else if (
        this.portfolioState !== BfxAutoCompleteComponentState.INVALID &&
        this.themeState !== BfxAutoCompleteComponentState.INVALID
      ) {
        state = BfxUserSettingsValidityState.VALID;
      } else {
        state = BfxUserSettingsValidityState.INVALID;
      }
      resolve(state);
    });
  }

  public setZoomValue(val: string): void {
    let zoom: any[] = ZOOM_VALUES.filter((value: any) => value.value == val);
    if (zoom.length == 1) {
      this.fontSize = zoom[0];
    }
  }

  public reset(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      Promise.all([
        this.parentComponent.activeUserService.getActiveUserData(),
        this.positionBookService.getAdaptedUserDefaultPositionBook(),
      ]).then(([activeUser, portfolio]) => {
        // MARKUP CONFIG
        this.searchPortfolio({ query: "" });
        this.filterTheme({ query: "" });
        this.searchFontSize({ query: "" });

        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(ProfileSettingKeys.userThemeSettings)
        ) {
          let themeLabel = activeUser.profileSettings.get(ProfileSettingKeys.userThemeSettings);
          this.setTheme(themeLabel);
          this.themeSelection.writeValue(
            this.themeSelection._suggestions.find((s) => s.value === themeLabel),
          );
        } else {
          this.theme = THEMES[0];
          this.themeSelection.writeValue(THEMES[0]);
        }

        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(ProfileSettingKeys.userSaveProfileOnLogout)
        ) {
          let saveProfileOnLogoutStr: string = activeUser.profileSettings.get(
            ProfileSettingKeys.userSaveProfileOnLogout,
          );
          this.saveProfileOnLogout = saveProfileOnLogoutStr === "true";
        } else {
          this.saveProfileOnLogout = true;
        }

        if (
          activeUser.profileSettings &&
          activeUser.profileSettings.has(ProfileSettingKeys.userZoomSetting)
        ) {
          let zoomValue: string = activeUser.profileSettings.get(
            ProfileSettingKeys.userZoomSetting,
          );
          this.setZoomValue(zoomValue);
          if (this.fontSizeSelection)
            this.fontSizeSelection.writeValue(
              this.fontSizeSelection._suggestions.find((s) => s.value === zoomValue),
            );
        } else {
          this.setZoomValue(ZOOM_VALUE_STANDARD.value);
          if (this.fontSizeSelection) this.fontSizeSelection.writeValue(ZOOM_VALUE_STANDARD);
        }

        this.portfolio = portfolio;
        let portfolioItem =
          this.portfolio != null
            ? this.portfolioSelection._suggestions.find((s) => s.value === this.portfolio.value)
            : null;
        this.portfolioSelection.writeValue(portfolioItem);

        this.autocompletes.forEach((item) => {
          if (item === this.themeSelection) {
            this.themeState = this.themeSelection.state;
          }
          if (item === this.portfolioSelection) {
            this.portfolioState = this.portfolioSelection.state;
          }
        });

        this.cd.detectChanges();

        resolve();
      });
    });
  }

  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: ProfileUpdateRequestImpl,
  ): CubiqTS.ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let popupSettingList: any = [];
    let savedPopUpSetting: CubiqTS.ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedPopUpSetting;
  }

  public save(request: ProfileUpdateRequestImpl): void {
    let themeSetting: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      ProfileSettingKeys.userThemeSettings,
      request,
    );
    let fontSizeSetting: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      ProfileSettingKeys.userZoomSetting,
      request,
    );
    let saveProfileOnLogoutSetting: CubiqTS.ProfileSetting = this.getProfileSettingFromKey(
      ProfileSettingKeys.userSaveProfileOnLogout,
      request,
    );

    if (this.theme != null) {
      if (themeSetting == null) {
        let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
        settingObject.profileSettingKey = ProfileSettingKeys.userThemeSettings;
        settingObject.profileSettingValue = this.theme.value;
        request.settings.push(settingObject);
      } else if (this.theme != null) {
        themeSetting.profileSettingValue = this.theme.value;
      }
    } else if (themeSetting != null) {
      // remove themeSetting as profileSettingValue must not be null
      request.settings = request.settings.filter((setting) => {
        return setting.profileSettingKey != ProfileSettingKeys.userThemeSettings;
      });
    }

    if (this.fontSize != null) {
      if (fontSizeSetting == null) {
        let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
        settingObject.profileSettingKey = ProfileSettingKeys.userZoomSetting;
        settingObject.profileSettingValue = this.fontSize.value;
        request.settings.push(settingObject);
      } else if (this.fontSize != null) {
        fontSizeSetting.profileSettingValue = this.fontSize.value;
      }
    }

    if (saveProfileOnLogoutSetting == null) {
      let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
      settingObject.profileSettingKey = ProfileSettingKeys.userSaveProfileOnLogout;
      settingObject.profileSettingValue = this.saveProfileOnLogout + "";
      request.settings.push(settingObject);
    } else {
      saveProfileOnLogoutSetting.profileSettingValue = this.saveProfileOnLogout + "";
    }

    if (this.portfolio != null && this.portfolio.value != null) {
      request.defaultPositionBookId = Long.fromValue(this.portfolio.value);
    } else {
      request.defaultPositionBookId = null;
    }
  }

  public postReset(): void {
    this.dataChanged = false;
  }

  public postSave(_request: ProfileUpdateRequestImpl): void {
    this.dataChanged = false;

    let activeUser = this.parentComponent.activeUser;

    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(ProfileSettingKeys.userThemeSettings, this.theme.value);
      activeUser.profileSettings.set(ProfileSettingKeys.userZoomSetting, this.fontSize.value);
      activeUser.profileSettings.set(
        ProfileSettingKeys.userSaveProfileOnLogout,
        this.saveProfileOnLogout + "",
      );
    }

    if (this.portfolio != null && this.portfolio.value != null) {
      activeUser.defaultPositionBookId = this.portfolio.value;
      activeUser.defaultPositionBookName = this.portfolio.label;
    } else {
      activeUser.defaultPositionBookId = null;
      activeUser.defaultPositionBookName = null;
    }

    if (this.loadedData) {
      this.loadedData.theme = this.theme;
      this.loadedData.portfolio = this.portfolio;
    }

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      NativeDesktopSupport.setZoomLevel(this.fontSize.zoom);
    }
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {
    this.panelHeight = componentState.componentBounds.height - 120;
  }

  public openChangePassword(): void {
    this.authService?.requestChangePassword();
  }
}
